import React from 'react';
import { Icon, Tooltip } from 'antd';
import { ColumnProps, SortOrder } from 'antd/lib/table';
import { Sorting } from 'src/types/Sorting';
import { QaErrorDetails } from '../../types/QaErrors';
import styles from './QaErrorsDetailsTable.module.less';

export const defaultPagination = {
  hideOnSinglePage: false,
  showSizeChanger: true,
  pageSizeOptions: ['10', '20', '50'],
  locale: { items_per_page: '' },
};
export type ColumnId = keyof QaErrorDetails;

export type ColumnSorting = Sorting<QaErrorDetails> & {
  field: ColumnId;
};

type StandardColumn = ColumnProps<QaErrorDetails>;

type FilterableColumn = StandardColumn & { dataIndex: ColumnId };

export type QaErrorsDetailsColumn = FilterableColumn | StandardColumn;

export type ColumnsFilterOption = {
  id: ColumnId;
  name: string;
  disabled?: boolean;
};

type Days = '2' | '7' | '15';

export const defaultColumnIds: ColumnId[] = [
  'errorCode',
  'currentErrorAvg',
  'errorAvg2Days',
  'errorAvg7Days',
  'errorAvg15Days',
];

export const columnsFilterOptions: ColumnsFilterOption[] = [];

const renderDifference = (record: QaErrorDetails, days: Days) => {
  const currentValue = Number(record.currentErrorAvg);
  const pastValue = Number(record[`errorAvg${days}Days`]);

  const difference = (((currentValue - pastValue) / pastValue) * 100).toFixed(2);
  const differencePercent = parseFloat(difference);

  const isErrorAvgValid = record[`errorAvg${days}Days`] !== null && !isNaN(parseFloat(record[`errorAvg${days}Days`]));

  const tooltipContent = isNaN(differencePercent)
    ? 'Current Day Average is : N/A'
    : `Current Day Average is ${
        isFinite(differencePercent)
          ? Math.abs(differencePercent).toLocaleString() + (differencePercent !== 0 ? '%' : '')
          : ''
      } ${
        differencePercent > 0 ? 'higher than ' : differencePercent < 0 ? 'lower than' : 'equal to'
      } historical ${days}-day`;

  const inputsTooltipContent = `${days}-Day Average Inputs: ${
    isNaN(parseFloat(record[`inputsAvg${days}Days`])) || !isFinite(parseFloat(record[`inputsAvg${days}Days`]))
      ? 'N/A'
      : parseFloat(record[`inputsAvg${days}Days`]).toLocaleString()
  }`;

  const errorValue =
    record[`errorAvg${days}Days`] !== null
      ? parseFloat(record[`errorAvg${days}Days`]) >= 1000
        ? parseFloat(record[`errorAvg${days}Days`]).toLocaleString()
        : record[`errorAvg${days}Days`]
      : 'N/A';

  return (
    <>
      <span className={styles.spaceColunm}>
        <Tooltip title={inputsTooltipContent}>
          <span className={styles.value}>{errorValue}</span>
        </Tooltip>
      </span>
      {isErrorAvgValid && (
        <Tooltip title={tooltipContent} className={styles.smallFont}>
          (
          <span>
            <Icon
              className={
                isFinite(Math.abs(differencePercent))
                  ? differencePercent > 0
                    ? styles.higher
                    : differencePercent < 0
                    ? styles.lower
                    : ''
                  : ''
              }
              type={
                isFinite(Math.abs(differencePercent))
                  ? differencePercent > 0
                    ? 'arrow-up'
                    : differencePercent < 0
                    ? 'arrow-down'
                    : ''
                  : ''
              }
            />
            {isFinite(Math.abs(differencePercent))
              ? differencePercent !== 0
                ? `${(Math.abs(differencePercent)).toLocaleString()}%`
                : '-'
              : '-'}
          </span>
          )
        </Tooltip>
      )}
    </>
  );
};

const columns: FilterableColumn[] = [
  {
    title: 'Error Code',
    dataIndex: 'errorCode',
    sorter: true,
    render: (_, record: QaErrorDetails) => {
      const inputsTooltipContent = `Error Code Message: ${
        record.errorMsg === undefined ? record.errorCode : record.errorMsg
      }`;

      return <Tooltip title={inputsTooltipContent}>{record.errorCode}</Tooltip>;
    },
  },
  {
    title: 'Error Rate (Current Day Average)',
    dataIndex: 'currentErrorAvg',
    sorter: true,
    render: (_, record: QaErrorDetails) => {
      const inputsTooltipContent = `Current Day Average Inputs: ${
        isNaN(parseFloat(record.currentInputs)) ? 'N/A' : parseFloat(record.currentInputs).toLocaleString()
      }`;

      return (
        <Tooltip title={inputsTooltipContent}>
          {record.currentErrorAvg === null ? 'N/A' : parseFloat(record.currentErrorAvg).toLocaleString()}
        </Tooltip>
      );
    },
  },
  {
    title: 'Error Rate (2-Day Average)',
    dataIndex: 'errorAvg2Days',
    sorter: true,
    render: (_, record: QaErrorDetails) => renderDifference(record, '2'),
  },
  {
    title: 'Error Rate (7-Day Average)',
    dataIndex: 'errorAvg7Days',
    sorter: true,
    render: (_, record: QaErrorDetails) => renderDifference(record, '7'),
  },
  {
    title: 'Error Rate (15-Day Average)',
    dataIndex: 'errorAvg15Days',
    sorter: true,
    render: (_, record: QaErrorDetails) => renderDifference(record, '15'),
  },
];

export const getColumns = (sorting: ColumnSorting): QaErrorsDetailsColumn[] => {
  const sortOrder = (key: ColumnId): SortOrder | false => {
    return sorting.field === key ? sorting.order : false;
  };

  const filteredColumns = columns.map((column) => ({
    ...column,
    ...(column.sorter && { sortOrder: sortOrder(column.dataIndex) }),
  }));

  return [...filteredColumns];
};
